import React from 'react';
import { toast } from 'react-toastify';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Button, Checkbox, FileUploader, Radio, Select, Text } from 'components';
import { Field, Form, Formik } from 'formik';
import { useQuery } from 'hooks/useQuery';
import { PageTitle } from 'pages/Layout/PageTitle';
import { emailSendingService, providerService } from 'services';

import { validationSchema } from './validation';

const weekdaysOptions = [
  { value: '1', label: 'Segunda' },
  { value: '2', label: 'Terça' },
  { value: '3', label: 'Quarta' },
  { value: '4', label: 'Quinta' },
  { value: '5', label: 'Sexta' },
];

const CreateEmailSendingConfig = () => {
  const [signageClients, , loadingSignageClients] = useQuery(
    () => providerService.findClientsExped(),
    [],
  );

  const signageClientsOptions = [{ label: 'TODOS', value: 0 }, ...(signageClients || [])];

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const formData = new FormData();

      formData.append('client', values.client);
      formData.append('title', values.title);
      formData.append('message', values.message);
      formData.append('recipients', values.recipients);
      formData.append('copy_recipients', values.copy_recipients || '');
      formData.append('weekday', values.weekday);
      formData.append('attachment_type', values.attachment_type);

      if (values.attachment_type === 'signage_report') {
        formData.append(
          'signage_report_clients',
          values.signageReportClients.includes(0) ? 'TODOS' : values.signageReportClients.join(';'),
        );
        formData.append('include_danfe_zip', values.include_danfe_zip);
      } else if (values.attachment) {
        formData.append('attachment', values.attachment);
      }

      await emailSendingService.createConfig(formData);

      toast.success('Configuração de envio de email cadastrada com sucesso!');

      resetForm();
    } catch (err) {
      console.log(err);
      toast.error(err.response?.data?.message || 'Erro ao cadastrar configuração.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageTitle>Nova configuração de envio de email</PageTitle>
      <Formik
        initialValues={{
          client: '',
          recipients: '',
          copy_recipients: '',
          title: '',
          message: '',
          weekday: '',
          attachment: null,
          attachment_type: 'manual',
          signageReportClients: [0],
          include_danfe_zip: false,
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(props) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6} lg={4}>
                <Field
                  variant='outlined'
                  label='Cliente'
                  name='client'
                  size='small'
                  component={Text}
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Field
                  variant='outlined'
                  label='Destinatário'
                  name='recipients'
                  size='small'
                  component={Text}
                  helperText='Separe múltiplos emails por ponto e vírgula ";"'
                />
              </Grid>

              <Grid item xs={12} md={6} lg={4}>
                <Field
                  variant='outlined'
                  label='Cópia'
                  name='copy_recipients'
                  size='small'
                  component={Text}
                  helperText='Separe múltiplos emails por ponto e vírgula ";"'
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  variant='outlined'
                  label='Título'
                  name='title'
                  size='small'
                  component={Text}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  variant='outlined'
                  label='Mensagem'
                  name='message'
                  size='small'
                  multiline
                  rows={6}
                  component={Text}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant='subtitle2'>Enviar automaticamente no dia</Typography>
              </Grid>
              {weekdaysOptions.map((weekday) => (
                <Grid item xs={6} md={2} key={weekday.value}>
                  <Field
                    name='weekday'
                    type='radio'
                    value={weekday.value}
                    label={weekday.label}
                    component={Radio}
                  />
                </Grid>
              ))}

              <Grid item xs={12}>
                <Typography variant='subtitle2'>Anexo</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Field
                  name='attachment_type'
                  type='radio'
                  value='manual'
                  label='Anexo manual'
                  component={Radio}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <Field
                  name='attachment_type'
                  type='radio'
                  value='signage_report'
                  label='Relatório de Sinalização (Expedição)'
                  component={Radio}
                />
              </Grid>

              {props.values.attachment_type === 'manual' && (
                <Grid item xs={12} md={6}>
                  <Field name='attachment' component={FileUploader} />
                </Grid>
              )}

              {props.values.attachment_type === 'signage_report' && (
                <Grid item xs={12} md={6}>
                  <Field
                    name='signageReportClients'
                    label='Clientes vinculados'
                    component={Select}
                    variant='outlined'
                    fullWidth
                    size='small'
                    multiple
                    loading={loadingSignageClients}
                    options={signageClientsOptions.map((client) => ({
                      ...client,
                      disabled: props.values.signageReportClients.includes(0) && client.value !== 0,
                    }))}
                  />
                </Grid>
              )}

              {props.values.attachment_type === 'signage_report' && (
                <Grid item xs={12}>
                  <Field
                    name='include_danfe_zip'
                    label='Anexar DANFEs das notas em ZIP'
                    component={Checkbox}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={4} lg={2}>
                <Button
                  type='submit'
                  disabled={!props.isValid || !props.dirty}
                  loading={props.isSubmitting}
                  fullWidth
                  onClick={props.handleSubmit}
                >
                  Cadastrar
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateEmailSendingConfig;
